<template>
  <svg
    :viewBox="`0 0 ${width} ${height2}`"
    style="display: block"
    class="caption"
  >
    <g :transform="`translate(0,${height2 - margin2.bottom})`">
      <g v-for="t in xTicks" :key="t" :transform="`translate(${x2(t)},0)`">
        <text fill="currentColor" y="9" dy="0.71em" text-anchor="middle">
          {{ t }}
        </text>
        <line
          stroke="currentColor"
          stroke-opacity="0.3"
          :y1="-(height2 - margin2.top - margin2.bottom)"
        ></line>
      </g>
    </g>
    <g :transform="`translate(${margin2.left},0)`">
      <g v-for="t in yTicks" :key="t" :transform="`translate(0,${y2(t)})`">
        <text fill="currentColor" x="-9" dy="0.32em" text-anchor="end">
          {{ formatYTick(t) }}
        </text>
        <line
          stroke="currentColor"
          stroke-opacity="0.3"
          :x1="width - margin2.left - margin2.right"
        ></line>
      </g>
    </g>
    <g>
      <g
        v-for="s in lineData"
        :key="s.key"
        :opacity="isItemOpaque(s.key) ? 0.05 : 1"
      >
        <path :stroke="s.color" fill="none" :d="linePath(s.values)"></path>
        <circle
          v-for="d in s.values.filter(d => d[yValueMode])"
          :key="d.year"
          :fill="s.color"
          :r="dotRadius"
          :cx="x2(d.year)"
          :cy="y2(d[yValueMode])"
        ></circle>
      </g>
    </g>
    <g :transform="`translate(0,${margin2.top})`" class="brush" ref="brush"></g>
  </svg>
</template>

<script>
import { scaleLinear } from "d3-scale";
import { max } from "d3-array";
import { line } from "d3-shape";
import { brushX } from "d3-brush";
import { select, event } from "d3-selection";

const getEvent = () => event;

export default {
  props: {
    width: {
      type: Number,
      required: true
    },
    yearRange: {
      type: Array,
      required: true
    },
    brushedYearRange: {
      type: Array,
      required: true
    },
    lineData: {
      type: Array,
      required: true
    },
    isItemOpaque: {
      type: Function,
      required: true
    },
    yValueMode: {
      type: String,
      required: true
    },
    formatYTick: {
      type: Function,
      required: true
    }
  },
  data: () => ({
    height2: 80,
    margin2: { top: 8, right: 20, bottom: 20, left: 40 },
    dotRadius: 4,
    brushstartedYearRange: []
  }),
  computed: {
    x2() {
      return scaleLinear()
        .domain([this.yearRange[0] - 0.5, this.yearRange[1] + 0.5])
        .range([this.margin2.left, this.width - this.margin2.right]);
    },
    y2() {
      return scaleLinear()
        .domain([
          0,
          max(this.lineData, d => max(d.values, d => d[this.yValueMode]))
        ])
        .range([
          this.height2 - this.margin2.bottom - this.dotRadius,
          this.margin2.top + this.dotRadius
        ]);
    },
    linePath() {
      return line()
        .x(d => this.x2(d.year))
        .y(d => this.y2(d[this.yValueMode]));
    },
    xTicks() {
      return this.x2.ticks(
        (this.width - this.margin2.left - this.margin2.right) / 80
      );
    },
    yTicks() {
      let yTicks = this.y2.ticks(this.height2 / 40);
      if (this.yValueMode.startsWith("count")) {
        yTicks = yTicks.filter(i => i % 1 === 0);
      }
      return yTicks;
    },
    brush() {
      return brushX()
        .extent([
          [this.margin2.left, -0.5],
          [
            this.width - this.margin2.right,
            this.height2 - this.margin2.bottom - this.margin2.top + 0.5
          ]
        ])
        .on("start", this.brushstarted)
        .on("brush", this.brushed)
        .on("end", this.brushended);
    }
  },
  watch: {
    x2() {
      select(this.$refs.brush).call(this.brush);
      this.syncBrush();
    },
    brushedYearRange() {
      this.syncBrush();
    }
  },
  mounted() {
    const gBrush = select(this.$refs.brush).call(this.brush);
    gBrush
      .selectAll(".handle--custom")
      .data([{ type: "w" }, { type: "e" }])
      .enter()
      .append("path")
      .attr("class", "handle--custom")
      .attr("stroke", "currentColor")
      .attr("cursor", "ew-resize")
      .attr("d", this.brushResizePath);
    gBrush.call(this.brush.move, this.x2.range());
  },
  methods: {
    brushSelectionToYearRange(s) {
      return s
        .map(this.x2.invert, this.x2)
        .map((d, i) => (i ? Math.floor(d) : Math.ceil(d)));
    },
    syncBrush() {
      select(this.$refs.brush).call(
        this.brush.move,
        this.brushedYearRange
          .map((d, i) => (i ? d + 0.5 : d - 0.5))
          .map(this.x2, this.x2)
      );
    },
    brushstarted() {
      this.brushstartedYearRange = this.brushedYearRange.slice();
      this.brushed();
    },
    brushed() {
      const s = getEvent().selection;
      if (s) {
        const ty = -(this.height2 - this.margin2.bottom - this.margin2.top) / 4;
        select(this.$refs.brush)
          .selectAll(".handle--custom")
          .attr("transform", (d, i) => "translate(" + [s[i], ty] + ")");
      }
    },
    brushended() {
      // Snap brush to year gaps
      const event = getEvent();
      const sourceEvent = event.sourceEvent;
      if (!sourceEvent || sourceEvent.type === "end") return; // Break infinite loop
      const s = event.selection || this.x2.range();
      const brushedYearRange = this.brushSelectionToYearRange(s);
      if (
        JSON.stringify(brushedYearRange) !==
        JSON.stringify(this.brushstartedYearRange)
      ) {
        this.$emit("brush-ended", brushedYearRange);
      }
    },
    brushResizePath(d) {
      const e = +(d.type == "e"),
        x = e ? 1 : -1,
        y = (this.height2 - this.margin2.top - this.margin2.bottom) / 2;
      return (
        "M" +
        0.5 * x +
        "," +
        y +
        "A6,6 0 0 " +
        e +
        " " +
        6.5 * x +
        "," +
        (y + 6) +
        "V" +
        (2 * y - 6) +
        "A6,6 0 0 " +
        e +
        " " +
        0.5 * x +
        "," +
        2 * y +
        "Z" +
        "M" +
        2.5 * x +
        "," +
        (y + 8) +
        "V" +
        (2 * y - 8) +
        "M" +
        4.5 * x +
        "," +
        (y + 8) +
        "V" +
        (2 * y - 8)
      );
    }
  }
};
</script>

<style scoped></style>
